import { makeObservable, observable, action } from 'mobx'
import { BaseStore } from './BaseStore'
import type { RoomStore, WSEvent } from './RoomStore'
import { config } from '@/lib/config'

export type WSStatus = 'disconnected' | 'connecting' | 'connected'

const RECONNECT_BASE_DELAY_MS = 1000
const RECONNECT_MAX_DELAY_MS = 30000

export class WebSocketStore extends BaseStore {
  status: WSStatus = 'disconnected'

  private ws: WebSocket | null = null
  private roomStore: RoomStore | null = null
  private params: Record<string, string> = {}
  private reconnectAttempts = 0
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null

  constructor() {
    super()
    makeObservable(this, {
      status: observable,
      connect: action,
      disconnect: action,
      setStatus: action,
    })
  }

  setStatus(status: WSStatus) {
    this.status = status
  }

  bind(roomStore: RoomStore) {
    this.roomStore = roomStore
  }

  get isEnabled() {
    return config.chatWsUrl !== ''
  }

  connect(params: Record<string, string>) {
    if (!this.isEnabled) {
      // Realtime is disabled (local dev); the UI falls back to polling.
      return
    }

    this.params = params
    this.disconnectInternal()
    this.setStatus('connecting')

    const query = new URLSearchParams(params).toString()
    const url = `${config.chatWsUrl}?${query}`

    try {
      const ws = new WebSocket(url)
      this.ws = ws

      ws.onopen = () => {
        this.reconnectAttempts = 0
        this.setStatus('connected')
      }

      ws.onmessage = (event) => {
        try {
          const parsed = JSON.parse(event.data as string) as WSEvent
          if (parsed && typeof parsed.type === 'string') {
            this.roomStore?.applyEvent(parsed)
          }
        } catch {
          // ignore malformed payloads
        }
      }

      ws.onclose = () => {
        if (this.ws === ws) {
          this.ws = null
          this.setStatus('disconnected')
          this.scheduleReconnect()
        }
      }

      ws.onerror = () => {
        ws.close()
      }
    } catch (error) {
      console.error('WebSocket connection failed:', error)
      this.setStatus('disconnected')
      this.scheduleReconnect()
    }
  }

  send(data: string): boolean {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(data)
      return true
    }
    return false
  }

  disconnect() {
    this.reconnectAttempts = 0
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer)
      this.reconnectTimer = null
    }
    this.disconnectInternal()
    this.setStatus('disconnected')
  }

  private disconnectInternal() {
    if (this.ws) {
      const ws = this.ws
      this.ws = null
      ws.onclose = null
      ws.onerror = null
      ws.onmessage = null
      ws.close()
    }
  }

  private scheduleReconnect() {
    if (!this.isEnabled || this.reconnectTimer) return

    const delay = Math.min(
      RECONNECT_BASE_DELAY_MS * Math.pow(2, this.reconnectAttempts),
      RECONNECT_MAX_DELAY_MS
    )
    this.reconnectAttempts += 1

    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null
      this.connect(this.params)
    }, delay)
  }
}